import type { CredentialingStatus } from '../../types';

export const CREDENTIALING_PAGE_SIZE = 25;
export const CREDENTIALING_STATUS_LABELS: Record<CredentialingStatus, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  complete: 'Complete',
};

/** Why a payer is on the credentialing list at all. */
export const ROSTER_CAPTION = 'From the intake form: wants to get credentialed';
export const OFF_ROSTER_TITLE = 'Not on the intake roster';
export const OFF_ROSTER_BLURB =
  'Records for payers the practice did not mark "Wants to get credentialed" on the intake form.';
export const UNRESOLVED_PAYER_NOTICE =
  'This payer is not matched to the payer directory yet, so a status cannot be recorded. Add it with the form below.';
/** Shown for a roster payer no provider has a record against yet. */
export const NO_PROVIDER_RECORDS = 'No provider records yet';

/** The rolled-up state of a practice's credentialing, for the summary card. */
export type CredentialingRollup =
  | 'not_started'
  | 'in_progress'
  | 'complete'
  | 'not_included'
  | 'unknown';

/**
 * Roll a page of per-provider rows up to one practice-level state.
 *
 * The list is paginated, so "complete" is only claimable from a first page
 * that fits in one page: anything else may hide an unfinished row, and the
 * card says so by staying at "in progress". A failed or in-flight read is
 * `unknown` — the card shows no pill rather than an invented one.
 *
 * `rosterPayersWithoutRecord` counts payers the practice asked to be
 * credentialed for on the intake form that have no record at all. Those are
 * not started, so they can never be rolled up as complete; passing nothing
 * keeps the record-only behaviour the panel had before the roster existed.
 */
export function rollUpCredentialingStatus(
  rows: readonly { status: CredentialingStatus }[],
  {
    offset,
    pending,
    failed,
    rosterPayersWithoutRecord = 0,
  }: {
    offset: number;
    pending: boolean;
    failed: boolean;
    rosterPayersWithoutRecord?: number;
  },
): CredentialingRollup {
  if (pending || failed) return 'unknown';
  if (rows.length === 0) return offset === 0 ? 'not_started' : 'in_progress';
  // A roster payer with no record has not started, so the practice cannot be
  // complete however finished the records that do exist look.
  if (rosterPayersWithoutRecord > 0) return 'in_progress';
  const wholeList = offset === 0 && rows.length < CREDENTIALING_PAGE_SIZE;
  if (wholeList && rows.every(row => row.status === 'complete')) return 'complete';
  return 'in_progress';
}
